import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { authApi } from '@/api/auth'
import { platformApi } from '@/api/platform'
import { useAuthStore } from '@/stores/authStore'
import { usePlatformStore } from '@/stores/platformStore'
import { applyAuthenticated } from '@/lib/slackAuth'

export default function GlobalLogin() {
  const navigate = useNavigate()
  const { isAuthenticated, org_slug, setAuth } = useAuthStore()
  const { isPlatformAuthed, setPlatform } = usePlatformStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [slackLoading, setSlackLoading] = useState(false)

  // Already signed in → skip the login page.
  useEffect(() => {
    if (isPlatformAuthed) navigate('/admin/platform', { replace: true })
    else if (isAuthenticated && org_slug) navigate(`/${org_slug}`, { replace: true })
  }, [isAuthenticated, org_slug, isPlatformAuthed, navigate])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const result = await authApi.discoverLogin(email.trim().toLowerCase(), password)

      if (result.status === 'platform') {
        setPlatform(result.access_token!, null)
        try {
          const me = await platformApi.me()
          setPlatform(result.access_token!, me)
        } catch { /* name is cosmetic */ }
        navigate('/admin/platform')
        return
      }

      if (result.status === 'select_org') {
        sessionStorage.setItem(
          'slack_org_select',
          JSON.stringify({ selection_token: result.selection_token, organizations: result.organizations }),
        )
        navigate('/select-org')
        return
      }

      applyAuthenticated(result, setAuth, navigate)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Invalid email or password')
      setLoading(false)
    }
  }

  const signInWithSlack = async () => {
    setSlackLoading(true)
    setError(null)
    try {
      const { url } = await authApi.slackOAuthUrl()
      window.location.href = url
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Slack sign-in is not available')
      setSlackLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 py-12 px-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/"><img src="/logo-dark.png" alt="Managition" className="h-9 mx-auto mb-4" /></Link>
            <h1 className="text-xl font-bold text-gray-900">Sign in to Managition</h1>
            <p className="mt-1 text-sm text-gray-500">
              Enter your email and password — we'll show the organizations you can access.
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 text-center">{error}</div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="label">Email address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
              />
            </div>
            <div>
              <label htmlFor="password" className="label">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input mt-1"
              />
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary w-full">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Continue'}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-2 bg-gray-50 text-gray-500">Or</span></div>
          </div>

          <button
            type="button"
            disabled={slackLoading}
            onClick={signInWithSlack}
            className="btn btn-outline w-full"
          >
            {slackLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
              </svg>
            )}
            Sign in with Slack
          </button>

          <p className="text-center text-xs text-gray-400">
            Multiple organizations under one email? Sign in once and pick which to enter.
          </p>
        </div>
      </div>

      {/* Footer — links required for the Slack Marketplace listing */}
      <footer className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-400">
        <Link to="/" className="hover:text-gray-600">About</Link>
        <Link to="/privacy" className="hover:text-gray-600">Privacy</Link>
        <Link to="/terms" className="hover:text-gray-600">Terms</Link>
        <Link to="/support" className="hover:text-gray-600">Support</Link>
      </footer>
    </div>
  )
}
